import { toLonLat } from 'ol/proj';
import { map as mainMap } from './Map';
import logoImg from '../../assets/Logo/Logo.png';
import OLMap from 'ol/Map';
import View from 'ol/View';

/**
 * Helper to convert image path to Base64 for reliable html2canvas rendering
 */
async function getBase64Image(src) {
    if (!src) return "";
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => {
            const img2 = new Image();
            img2.crossOrigin = 'anonymous';
            img2.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img2.width;
                canvas.height = img2.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img2, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img2.onerror = reject;
            img2.src = src;
        };
        img.src = src;
    });
}

/**
 * North Arrow SVG
 */
const NORTH_ARROW_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="40" height="40">
  <polygon points="25,5 30,20 25,15 20,20" fill="#c41e3a" stroke="#fff" stroke-width="1"/>
  <polygon points="25,15 30,20 25,45 20,20" fill="#1e293b" stroke="#fff" stroke-width="1"/>
  <text x="25" y="50" text-anchor="middle" font-size="10" font-weight="bold" fill="#1e293b">N</text>
</svg>
`;

async function generateParcelReport(parcel, feature) {
    if (!window.html2canvas) {
        alert('مكتبة التصدير غير محملة. تحقق من الإنترنت.');
        return;
    }

    const originalBtn = document.getElementById('genReportBtn');
    let originalText = '';
    if (originalBtn) {
        originalText = originalBtn.innerHTML;
        originalBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> جاري التجهيز...';
        originalBtn.disabled = true;
    }

    // Temporary container for off-screen map
    const mapContainer = document.createElement('div');
    mapContainer.style.width = '800px';
    mapContainer.style.height = '600px';
    mapContainer.style.position = 'absolute';
    mapContainer.style.top = '-10000px';
    mapContainer.style.left = '-10000px';
    document.body.appendChild(mapContainer);

    try {
        // 1. Prepare Images
        let logoBase64 = "";
        try {
            const headerLogo = document.querySelector('.logo-header');
            if (headerLogo && headerLogo.src) {
                logoBase64 = await getBase64Image(headerLogo.src);
            } else {
                logoBase64 = await getBase64Image(logoImg);
            }
        } catch (e) {
            console.warn("Logo failed to load as Base64", e);
            try { logoBase64 = await getBase64Image("assets/Logo/Logo.png"); } catch (e2) { }
        }

        // 1.1 Create Off-Screen Map (Fixes "glitchy" main map jumps)
        let mapImage = "";
        try {
            const mainView = mainMap.getView();

            // Recursive helper to clone layers and groups
            const cloneLayer = (layer) => {
                if (layer.getLayers && typeof layer.getLayers === 'function') {
                    const innerLayers = layer.getLayers().getArray().map(cloneLayer).filter(l => l !== null);
                    if (innerLayers.length === 0) return null;
                    const GroupClass = layer.constructor;
                    return new GroupClass({
                        layers: innerLayers,
                        visible: layer.getVisible(),
                        opacity: layer.getOpacity(),
                        zIndex: layer.getZIndex()
                    });
                }

                if (typeof layer.getSource !== 'function') return null;

                const LayerClass = layer.constructor;
                const source = layer.getSource();
                if (!source) return null;

                return new LayerClass({
                    source: source,
                    visible: layer.getVisible(),
                    opacity: layer.getOpacity(),
                    zIndex: layer.getZIndex(),
                    style: (typeof layer.getStyle === 'function') ? layer.getStyle() : undefined
                });
            };

            const offLayers = mainMap.getLayers().getArray().map(cloneLayer).filter(l => l !== null);

            const offScreenMap = new OLMap({
                target: mapContainer,
                layers: offLayers,
                view: new View({
                    projection: mainView.getProjection(),
                    center: mainView.getCenter(),
                    zoom: mainView.getZoom(),
                    rotation: 0
                }),
                controls: []
            });

            // Fit the off-screen view to the parcel
            if (feature && feature.getGeometry()) {
                offScreenMap.getView().fit(feature.getGeometry().getExtent(), {
                    padding: [100, 100, 100, 100],
                    duration: 0
                });
            }

            // Wait for off-screen rendering
            await new Promise(resolve => {
                const onRender = () => {
                    offScreenMap.un('rendercomplete', onRender);
                    resolve();
                };
                offScreenMap.on('rendercomplete', onRender);
                offScreenMap.renderSync();
                setTimeout(resolve, 5000); // Wait for imagery
            });

            const mapViewport = offScreenMap.getViewport();
            const mapCanvas = mapViewport ? mapViewport.querySelector('canvas') : null;

            if (mapCanvas) {
                mapImage = mapCanvas.toDataURL('image/png', 1.0);
            } else {
                const fallbackCanvas = mapContainer.querySelector('canvas');
                if (fallbackCanvas) {
                    mapImage = fallbackCanvas.toDataURL('image/png', 1.0);
                }
            }

            // Cleanup off-screen map
            offScreenMap.setTarget(null);

        } catch (mapErr) {
            console.error("Map Capture Failed:", mapErr);
            // Non-blocking: continue without map image if valid tainted
            if (window.showErrorModal) window.showErrorModal('تحذير: فشل التقاط صورة الخريطة (CORS)، سيتم التصدير بدونها.');
        }


        // 2. Information
        const dateStr = new Date().toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });
        const darkRed = '#dc3545';
        const brandBlue = '#1e293b';

        // Helper Data Formatting
        const planVal = parcel.plan || parcel.Plan || '-';
        const planDisplay = (planVal === '-') ? 'لا يوجد مخطط هيكلي' : planVal;

        const slopeVal = parcel.slope_mean || parcel.Slope_Mean;
        const slopeDisplay = slopeVal ? `${parseFloat(slopeVal).toFixed(1)}°` : '-';

        const elvVal = parcel.elv || parcel.Elv;
        const elvDisplay = elvVal ? `${parseFloat(elvVal).toFixed(0)} م` : '-';

        const areaDisplay = parcel.area || parcel.Area ? parseFloat(parcel.area || parcel.Area).toLocaleString() : '-';

        // 3. HTML Template (A4: 794x1123)
        const reportHTML = `
            <div id="report-content" style="
                width: 794px; 
                min-height: 1123px;
                background: #ffffff; 
                font-family: 'Cairo', sans-serif; 
                direction: rtl; 
                position: absolute; 
                top: -15000px; 
                left: 0;
                display: flex;
                flex-direction: column;
                box-sizing: border-box;
            ">
                <!-- Header -->
                <div style="background: ${darkRed}; color: white; padding: 20px 40px; display: flex; align-items: center; justify-content: space-between;">
                    <div style="display: flex; align-items: center; gap: 15px;">
                        <h1 style="margin: 0; font-size: 28px; font-weight: 800;">GeoPST</h1>
                    </div>
                    <div style="text-align: left; font-size: 12px; opacity: 0.9;">
                        <div>تاريخ الإصدار: ${dateStr}</div>
                    </div>
                </div>

                <!-- Content -->
                <div style="padding: 40px 60px; flex: 1; display: flex; flex-direction: column;">
                    
                    <div style="text-align: center; margin-bottom: 30px;">
                        <h2 style="margin: 0; font-size: 24px; color: ${brandBlue}; font-weight: 700; border-bottom: 3px solid ${darkRed}; display: inline-block; padding-bottom: 5px;">تقرير بيانات مكانية - قطعة أرض</h2>
                    </div>

                    <!-- Map -->
                    <div style="margin-bottom: 25px; text-align: center;">
                        <div style="
                            border: 2px solid #e2e8f0; 
                            border-radius: 12px; 
                            overflow: hidden; 
                            background: #f8fafc;
                            padding: 6px;
                            position: relative;
                            width: 100%;
                            margin: 0 auto;
                        ">
                            ${mapImage ? `<img src="${mapImage}" style="width: 100%; height: auto; max-height: 550px; display: block; border-radius: 6px; object-fit: contain; background: #fff;">` : '<div style="height:300px; display:flex; align-items:center; justify-content:center; color:#999;">صورة الخريطة غير متوفرة</div>'}
                            <div style="position: absolute; top: 15px; left: 15px; background: rgba(255,255,255,0.9); border-radius: 6px; padding: 5px; box-shadow: 0 4px 10px rgba(0,0,0,0.15);">
                                ${NORTH_ARROW_SVG}
                            </div>
                        </div>
                        <p style="margin: 8px 0 0; font-size: 11px; color: #64748b;">خريطة الموقع والحدود العقارية</p>
                    </div>

                    <!-- Table -->
                    <div style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; margin-bottom: 20px;">
                        <div style="background: #f1f5f9; padding: 8px 20px; border-bottom: 1px solid #e2e8f0;">
                            <h3 style="margin: 0; font-size: 14px; color: ${darkRed}; font-weight: 700;">تفاصيل قطعة الأرض</h3>
                        </div>
                        <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
                            <tbody>
                                <tr style="border-bottom: 1px solid #f1f5f9;">
                                    <td style="padding: 8px 12px; width: 15%; color: #64748b; font-weight: 600; background: #fafafa;">المحافظة</td>
                                    <td style="padding: 8px 12px; width: 35%; color: ${brandBlue}; font-weight: 700;">${parcel.gov_a || '-'}</td>
                                    <td style="padding: 8px 12px; width: 15%; color: #64748b; font-weight: 600; background: #fafafa; border-right: 1px solid #f1f5f9;">رقم الحوض</td>
                                    <td style="padding: 8px 12px; width: 35%; color: ${darkRed}; font-weight: 700;">${parcel.block_no || '-'}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #f1f5f9;">
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa;">التجمع</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue}; font-weight: 700;">${parcel.village_a || '-'}</td>
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa; border-right: 1px solid #f1f5f9;">اسم الحوض</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue};">${parcel.block_a || '-'}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #f1f5f9;">
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa;">اسم الحي</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue};font-weight: 700;">${parcel.quarternam || parcel.QuarterNam || '-'}</td>
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa; border-right: 1px solid #f1f5f9;">رقم الحي</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue};">${parcel.quarternum || parcel.QuarterNum || '-'}</td>
                                </tr>
                                <tr style="border-bottom: 1px solid #f1f5f9;">
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa;">رقم القطعة</td>
                                    <td style="padding: 8px 12px; color: ${darkRed}; font-weight: 800; font-size: 16px;">${parcel.plot_no || '-'}</td>
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa; border-right: 1px solid #f1f5f9;">المساحة (م²)</td>
                                    <td style="padding: 8px 12px; color: ${darkRed}; font-weight: 800;">${areaDisplay}</td>
                                </tr>

                                <tr style="border-bottom: 1px solid #f1f5f9;">
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa;">التقسيم الجيوسياسي</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue};">${parcel.landclas_l || parcel.landclas_1 || parcel.LandClas_1 || '-'}</td>
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa; border-right: 1px solid #f1f5f9;">المخطط الهيكلي</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue};">${planDisplay}</td>
                                </tr>

                                <tr style="border-bottom: 1px solid #f1f5f9;">
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa;">نوع الأرض</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue};">${parcel.plot_type || parcel.PLOT_TYPE || '-'}</td>
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa; border-right: 1px solid #f1f5f9;">نوع الصخور</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue};">${parcel.rock_type_ || parcel.ROCK_TYPE_ || '-'}</td>
                                </tr>

                                <tr style="border-bottom: 1px solid #f1f5f9;">
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa;">رطوبة التربة</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue};">${parcel.moisture_c || parcel.MOISTURE_C || '-'}</td>
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa; border-right: 1px solid #f1f5f9;">الخدمات</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue};">${parcel.road_acces || parcel.ROAD_ACCES || '-'}</td>
                                </tr>

                                <tr style="border-bottom: 1px solid #f1f5f9;">
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa;">متوسط الميل</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue}; direction: ltr; text-align: right;">${slopeDisplay}</td>
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa; border-right: 1px solid #f1f5f9;">الارتفاع</td>
                                    <td style="padding: 8px 12px; color: ${brandBlue}; direction: ltr; text-align: right;">${elvDisplay}</td>
                                </tr>

                                <tr>
                                    <td style="padding: 8px 12px; color: #64748b; font-weight: 600; background: #fafafa;">ملاحظات</td>
                                    <td colspan="3" style="padding: 8px 12px; color: ${brandBlue};">${parcel.notes || parcel.NOTES || '-'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div style="margin-top: auto; display: flex; justify-content: space-between; align-items: flex-end; padding-top: 30px;">
                        <div style="width: 70%; font-size: 11px; color: #94a3b8; border-right: 4px solid #f1f5f9; padding-right: 20px; line-height: 1.6;">
                            هذا التقرير تم استخراجه آلياً من منصة GeoPST. البيانات الواردة هي لأغراض استرشادية، ولا يعتد بها كوثيقة رسمية أو سند ملكية.
                        </div>
                    </div>
                </div>

                <div style="background: #f8fafc; border-top: 1px solid #e2e8f0; padding: 15px 40px; display: flex; justify-content: space-between; align-items: center; color: #94a3b8; font-size: 11px;">
                    <div>GeoPST — الذكاء الجغرافي لفلسطين</div>
                    <div>${window.location.host}</div>
                </div>
            </div>
        `;

        // 4. Export
        const container = document.createElement('div');
        container.innerHTML = reportHTML;
        document.body.appendChild(container);

        await new Promise(r => setTimeout(r, 1200));
        const element = document.getElementById('report-content');

        const canvas = await window.html2canvas(element, {
            scale: 2.0,
            useCORS: true,
            windowWidth: 794,
            windowHeight: 1123,
            backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.9);

        // 5. Download Image
        const link = document.createElement('a');
        link.href = imgData;
        link.download = `GeoPST_Report_${parcel.plot_no || 'parcel'}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        document.body.removeChild(container);

    } catch (err) {
        console.error("Export Failed:", err);
        // Show actual error message
        alert(`فشل التصدير: ${err.message || err.toString()}`);
    } finally {
        if (mapContainer.parentNode) document.body.removeChild(mapContainer);
        if (originalBtn) {
            originalBtn.innerHTML = originalText;
            originalBtn.disabled = false;
        }
    }
}

window.generateParcelReport = generateParcelReport;
export { generateParcelReport };
